import { cva, type VariantProps } from "class-variance-authority";
import { Slot as SlotPrimitive } from "radix-ui";
import type React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        secondary: "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "min-h-[44px] px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5",
        lg: "min-h-[48px] rounded-md px-6 has-[>svg]:px-4",
        icon: "size-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    class?: string;
  };

/**
 * Button is a flexible, accessible button component built with Radix UI and CVA.
 * Supports multiple visual variants, sizes, and can render as any element via asChild.
 *
 * @param props - Button props extending native button attributes
 * @param props.variant - Visual style (default, destructive, outline, secondary, ghost, link)
 * @param props.size - Button size (default, sm, lg, icon)
 * @param props.asChild - Render as child element instead of button (for links, etc.)
 * @param props.className - Additional CSS classes
 * @returns JSX.Element
 *
 * @example
 * ```tsx
 * // Primary button
 * <Button>Submit</Button>
 *
 * // Destructive action
 * <Button variant="destructive">Delete</Button>
 *
 * // Small outline button
 * <Button variant="outline" size="sm">Cancel</Button>
 *
 * // Icon-only button
 * <Button variant="ghost" size="icon">
 *   <TrashIcon />
 * </Button>
 *
 * // Render as Link component
 * <Button asChild>
 *   <Link href="/dashboard">Dashboard</Link>
 * </Button>
 * ```
 *
 * Variants:
 * - **default**: Primary brand color with shadow
 * - **destructive**: Red for dangerous actions
 * - **outline**: Bordered with transparent background
 * - **secondary**: Muted secondary color
 * - **ghost**: Transparent with hover background
 * - **link**: Underlined text link style
 *
 * Features:
 * - Focus-visible ring for keyboard navigation
 * - Disabled state with reduced opacity
 * - Invalid state styling (aria-invalid)
 * - SVG icon sizing and pointer-events handling
 * - Dark mode support
 * - Responsive touch targets (44px minimum)
 *
 * @see {@link buttonVariants} for variant configuration
 */
function Button({
  className,
  class: legacyClassName,
  variant,
  size,
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? SlotPrimitive.Slot : "button";
  const resolvedClassName = cn(className, legacyClassName);

  return (
    <Comp
      className={buttonVariants({
        variant,
        size,
        className: resolvedClassName,
      })}
      data-slot="button"
      {...props}
    />
  );
}

export { Button, buttonVariants };
